import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import NewsletterCTA from "@/components/NewsletterCTA";
import { BLOG_POSTS, CATEGORIES } from "@/data/blogPosts";
import { ArrowUpRight, Search } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const BLOG_HERO = "https://images.unsplash.com/photo-1558005530-a7958896ec60?auto=format&fit=crop&w=2400&q=80";

export default function Blog() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const root = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BLOG_POSTS.filter((p) => {
      const matchesCat = category === "All" || p.category === category;
      const matchesQ =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q);
      return matchesCat && matchesQ;
    });
  }, [category, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".fade-up").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 90%" },
          }
        );
      });
    }, root);
    return () => ctx.revert();
  }, [filtered.length]);

  return (
    <SmoothScroll>
      <div ref={root} className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title="Journal — destination guides for travelers visiting Indonesia"
          description="Field notes, destination guides, and quiet tips for international travelers planning a journey through Indonesia. Bali, Komodo, Raja Ampat, Yogyakarta and beyond."
          image={BLOG_HERO}
        />
        <Navbar transparent />

        {/* Hero */}
        <section className="relative h-[78vh] min-h-[520px] w-full overflow-hidden">
          <img
            src={BLOG_HERO}
            alt="Indonesian rice terraces"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-[#FDFBF7]" />
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
            <div className="t-overline text-white/85 mb-4">The Journal</div>
            <h1 className="t-display text-white max-w-4xl">
              Stories from <b>across Indonesia</b>
            </h1>
            <p className="mt-6 max-w-xl text-white/85 text-base md:text-lg leading-relaxed">
              Field notes, destination guides, and quiet tips for international
              travelers planning a journey through the archipelago.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 -mt-16 relative z-20">
          <div className="bg-white rounded-3xl shadow-xl border border-stone-100 px-6 py-5 flex flex-col md:flex-row gap-5 items-stretch md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-xs tracking-wide uppercase font-medium transition-all ${
                    category === c
                      ? "bg-slate-900 text-white shadow-md"
                      : "bg-stone-50 text-slate-600 hover:bg-stone-100"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative md:w-72">
              <Search
                size={16}
                strokeWidth={1.5}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                data-testid="blog-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stories…"
                className="w-full pl-11 pr-4 py-2.5 rounded-full bg-stone-50 border border-stone-200 text-sm placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-seafoam transition-all"
              />
            </div>
          </div>
        </section>

        {/* Featured */}
        {featured && (
          <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20" data-testid="blog-featured">
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid md:grid-cols-12 gap-10 items-end fade-up"
            >
              <div className="md:col-span-7 relative overflow-hidden rounded-3xl aspect-[16/10] bg-stone-100">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
              </div>
              <div className="md:col-span-5">
                <div className="t-overline text-seafoam mb-4">{featured.category} · Featured</div>
                <h2 className="t-h2 text-slate-900">
                  <b>{featured.title}</b>
                </h2>
                <p className="mt-5 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-slate-500 uppercase tracking-wider">
                  <span>{featured.author}</span>
                  <span className="opacity-50">·</span>
                  <span>{featured.readTime}</span>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-slate-900 group-hover:text-seafoam transition-colors">
                  Read the story <ArrowUpRight size={16} strokeWidth={1.5} />
                </div>
              </div>
            </Link>
          </section>
        )}

        {/* Grid */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 pb-24">
          {rest.length === 0 ? (
            <div className="text-center py-32 text-slate-500">
              No stories yet for this filter. Try another category.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {rest.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group fade-up block"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-stone-100">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-white/85 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
                        {p.category}
                      </span>
                    </div>
                  </div>
                  <div className="mt-5">
                    <h3 className="text-xl font-light tracking-tight text-slate-900 leading-snug group-hover:text-seafoam transition-colors">
                      <b className="font-bold">{p.title.split(":")[0]}</b>
                      {p.title.includes(":") ? `:${p.title.split(":").slice(1).join(":")}` : ""}
                    </h3>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed line-clamp-2">
                      {p.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400">
                      <span>{p.author}</span>
                      <span className="opacity-50">·</span>
                      <span>{p.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <NewsletterCTA source="blog" />

        <Footer />
      </div>
    </SmoothScroll>
  );
}
